// src/i18n.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

const resources = {
  en: {
    translation: {
      dashboard: {
        title: 'Dashboard',
        myProjects: 'My Projects',
        allProjects: 'All Projects',
        newProject: 'New Project',
        searchProjects: 'Search projects...',
        noProjects: 'No projects found matching your criteria',
        showFilters: 'Show Filters',
        hideFilters: 'Hide Filters',
        filterByStatus: 'Filter by Status',
        showingProjects: 'Showing {{filtered}} of {{total}} projects',
        projectDetails: 'Project Details',
        errors: {
          fetchFailed: 'Failed to load projects',
          techniciansFetchFailed: 'Failed to load technicians',
          saveFailed: 'Failed to save project',
          deleteFailed: 'Failed to delete project'
        }
      },
      
      workLogs: {
        title: 'Work Logs',
        logHours: 'Log Work Hours',
        confirmDelete: 'Are you sure you want to delete this work log?',
        deleteFailed: 'Failed to delete work log',
        saveFailed: 'Failed to save work log',
        noProject: 'No project selected',
        newWorkLog: 'New Work Log',
        editWorkLog: 'Edit Work Log',
        date: 'Date',
        hours: 'Hours',
        description: 'Description',
        saving: 'Saving...',
        errors: {
          noProject: 'No project selected',
          saveWorkLog: 'Failed to save work log',
          deleteWorkLog: 'Failed to delete work log',
          requiredFields: 'Date and hours are required'
        }
      },
      
      files: {
        filesAndPhotos: 'Files & Photos',
        noFilesForTag: 'No files found for selected category', 
        noFiles: 'No files uploaded yet',
        noCategories: 'No file categories configured',
        title: 'Upload Files',
        category: 'File Category',
        selectCategory: 'Select a category',
        uploadFiles: 'Upload Files',
        preview: 'File preview',
        addMore: 'Add More',
        takePhoto: 'Take Photo',
        supportedFormats: 'Images, PDFs, Office documents up to 10MB',
        description: 'Description',
        descriptionPlaceholder: 'Add a description for the files...',
        uploading: 'Uploading...',
        uploadButton: 'Upload {{count}} File',
        uploadButton_plural: 'Upload {{count}} Files',
        deleteFile: 'Are you sure you want to delete this file?',
        downloadAll: 'Download All',
        download: 'Download File',
        file: 'File',
        textFile: 'Text file',
        unknown: 'Unknown',
        cannotPreview: 'This file type cannot be previewed directly. Please download the file to view it.',
        errors: {
          noFiles: 'Please select at least one file',
          invalidType: 'File type not supported',
          maxSize: 'File is too large. Maximum size is 10MB',
          uploadFailed: 'Failed to upload files',
          deleteFailed: 'Failed to delete file',
          noResponse: 'No response from server',
          invalidFormat: 'Invalid file data format'
        },
        dropFiles: 'Drop files here',
      },
      
      header: {
        title: 'Project Management System',
        profile: {
          language: 'Language',
          changePassword: 'Change Password',
          addToHomeScreen: 'Add to Home Screen',
          logout: 'Logout'
        },
        customers: 'Customers',
        warehouse: {
          title: 'Warehouse',
          items: 'Warehouse Items',
          materialReceipt: 'Material Receipt',
          materialDelivery: 'Material Delivery'
        },
        setup: {
          title: 'Setup',
          companySetup: 'Company Setup',
          userManagement: 'User Management',
          warehouseManagement: 'Warehouse Management'
        },
        languages: {
          en: 'English',
          cs: 'Czech'
        }
      },

      auth: {
        login: 'Sign in',
        logout: 'Sign out',
        username: 'Username',
        password: 'Password',
        loginTitle: 'Project Management System',
        loginSubtitle: 'Please sign in to your account',
        errors: {
          invalidCredentials: 'Invalid credentials',
          accountDeactivated: 'Account is deactivated'
        },
        passwordChange: {
          title: 'Change Password',
          currentPassword: 'Current Password',
          newPassword: 'New Password',
          confirmPassword: 'Confirm New Password',
          meetRequirements: 'Please meet all password requirements',
          mustBeDifferent: 'New password must be different from current password',
          success: 'Password changed successfully',
          error: 'Failed to change password',
          changing: 'Changing Password...',
          submit: 'Change Password',
          requirements: {
            title: 'Password Requirements',
            minLength: 'At least 8 characters long',
            hasUppercase: 'Contains at least one uppercase letter',
            hasLowercase: 'Contains at least one lowercase letter',
            hasNumber: 'Contains at least one number',
            hasSpecial: 'Contains at least one special character',
            matches: 'Passwords match'
          }
        }
      },

      userManagement: {
        title: 'User Management',
        newUser: 'New User',
        editUser: 'Edit User',
        users: 'Users',
        search: 'Search users...',
        username: 'Username',
        password: 'Password',
        name: 'Name',
        email: 'Email',
        role: 'System Role',
        companyRole: 'Company Role',
        status: 'Status',
        active: 'Active',
        roles: {
          admin: 'Administrator',
          officeAdmin: 'Office Administrator',
          warehouseAdmin: 'Warehouse Administrator',
          technician: 'Technician'
        },
        noUsersFound: 'No users found matching your search criteria.',
        usersTab: 'Users',
        companyRolesTab: 'Company Roles',
        newCompanyRole: 'New Company Role',
        editCompanyRole: 'Edit Company Role',
        roleName: 'Role Name',
        roleDescription: 'Description',
        usersWithRole: 'Users',
        noRolesFound: 'No company roles found.',
        selectCompanyRole: 'Select company role',
        enterCompanyRole: 'Enter company role',
        companyRoles: {
          electrician: 'Electrician',
          plumber: 'Plumber',
          hvac: 'HVAC Technician'
        },
        errors: {
          loadUsers: 'Failed to load users',
          saveUser: 'Failed to save user',
          deleteUser: 'Failed to delete user'
        }
      },

      warehouse: {
        title: 'Warehouse',
        items: {
          title: 'Warehouse Items',
          new: 'New Item',
          search: 'Search items...',
          noItems: 'No items found'
        },
        materialReceipt: {
          title: 'Material Receipts',
          new: 'New Receipt',
          search: 'Search receipts...',
          noReceipts: 'No receipts found'
        },
        materialDelivery: {
          title: 'Material Deliveries',
          new: 'New Delivery',
          search: 'Search deliveries...',
          noDeliveries: 'No deliveries found'
        }
      },

      customers: {
        title: 'Customers',
        new: 'New Customer',
        search: 'Search customers...',
        noCustomers: 'No customers found',
        type: {
          company: 'Company',
          personal: 'Personal'
        },
        form: {
          companyName: 'Company Name',
          taxId: 'Tax ID',
          contactPerson: 'Contact Person',
          fullName: 'Full Name',
          email: 'Email',
          phone: 'Phone',
          address: {
            title: 'Address',
            street: 'Street',
            city: 'City',
            postalCode: 'Postal Code',
            country: 'Country'
          }
        }
      },
      
      photos: {
        title: 'Upload Photos',
        photoType: 'Photo Type',
        tags: {
          all: 'All',
          inspection: 'Inspection',
          implementation: 'Implementation'
        },
        uploadPhotos: 'Photos',
        preview: 'Photo preview',
        addMore: 'Add More',
        takePhoto: 'Take Photo',
        uploadFiles: 'Upload Files',
        supportedFormats: 'PNG, JPG, GIF up to 10MB',
        description: 'Description (applies to all photos)',
        uploading: 'Uploading...',
        uploadButton: 'Upload {{count}} Photo',
        uploadButton_plural: 'Upload {{count}} Photos',
        errors: {
          invalidType: 'Please select only image files',
          noPhotos: 'Please select at least one image',
          uploadFailed: 'Error uploading photos',
          downloadFailed: 'Failed to download photos'
        },
        defaultAlt: 'Project photo',
        deletePhoto: 'Delete photo',
        noPhotosForTag: 'No photos for selected category',
        downloadAll: 'Download All'
      },
      
      projects: {
        unnamed: 'Unnamed Project',
        startDate: 'Start Date',
        status: 'Status',
        statusUnknown: 'Unknown',
        description: 'Description',
        assignedTechnicians: 'Assigned Technicians',
        customerSignature: 'Customer Signature',
        signatureFor: 'Signature for {{name}}',
        addSignature: 'Add Signature',
        updateSignature: 'Update Signature',
        signatureImage: 'Customer signature',
        signedOn: 'Signed on {{date}}',
        noSignature: 'No signature provided',
        
        // New project modal translations
        new: 'New Project',
        edit: 'Edit Project',
        name: 'Project Name',
        contractNumber: 'Contract Number',
        location: 'Location',
        deleteProject: 'Delete Project',
        selectTechnicians: 'Select technicians...',
        searchTechnicians: 'Search technicians...',
        noTechniciansFound: 'No technicians found',
        techniciansSelected: '{{count}} technician(s) selected',
        
        // Validation messages
        validation: {
          contractNumberRequired: 'Contract number is required',
          nameRequired: 'Project name is required',
          locationRequired: 'Location is required',
          startDateRequired: 'Start date is required'
        },
        
        errors: {
          loadProjects: 'Failed to load projects',
          loadTechnicians: 'Failed to load technicians',
          saveProject: 'Failed to save project',
          deleteProject: 'Failed to delete project'
        }
      },

      // Company Settings Translations
      companySetup: {
        title: 'Company Setup',
        description: 'Manage your company information, currencies, and file categories', // ✅ NOVÝ
        saved: 'Company settings saved successfully!',
        
        sections: {
          companyInfo: 'Company Information',
          basicInfo: 'Basic Information', // ✅ NOVÝ
          currencySettings: 'Currency Settings',
          fileCategories: 'File Categories'
        },
        
        fields: {
          name: 'Company Name',
          companyName: 'Company Name', // ✅ NOVÝ (alias)
          taxId: 'Tax ID',
          address: 'Address',
          city: 'City',
          postalCode: 'Postal Code',
          country: 'Country',
          phone: 'Phone',
          email: 'Email'
        },
        
        buttons: {
          save: 'Save Settings'
        },
        
        messages: {
          saved: 'Company settings saved successfully!' // ✅ NOVÝ
        },
        
        validation: {
          requiredFields: 'Please fill in all required fields: {{fields}}',
          defaultCurrency: 'Default currency must be enabled'
        },
        
        // Existující překlady - jen pro referenci, možná už máte
        errors: {
          loadSettings: 'Failed to load company settings',
          saveSettings: 'Failed to save company settings', 
          missingFields: 'Please fill in all required fields'
        }
      },

      // File Tags Management Translations
      fileTags: {
        title: 'File Categories',
        addTag: 'Add Category',
        tagCode: 'Tag Code',
        englishLabel: 'English Display Name',
        czechLabel: 'Czech Display Name',
        cannotDelete: 'Cannot delete system tag',
        missingAllTag: 'Warning: The "All" tag is missing. This tag is required for proper system functioning.',
        note: 'Note: The "All" tag cannot be deleted as it is required for the system. Other tags can be disabled or deleted as needed.'
      },

      // Currency Management Translations
      currency: {
        defaultCurrency: 'Default Currency',
        availableCurrencies: 'Available Currencies',
        searchCurrencies: 'Search currencies...'
      },

      common: {
        developerLogo: 'Developer Logo',
        customerLogo: 'Customer Logo',
        showMore: 'Show More',
        showLess: 'Show Less',
        actions: {
          save: 'Save',
          cancel: 'Cancel',
          edit: 'Edit',
          delete: 'Delete',
          create: 'Create',
          update: 'Update',
          search: 'Search',
          close: 'Close',
          confirm: 'Confirm',
          title: 'Actions'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive',
          pending: 'Pending',
          completed: 'Completed',
          onHold: 'On Hold',
          cancelled: 'Cancelled'
        },
        messages: {
          confirmDelete: 'Are you sure you want to delete this?',
          loading: 'Loading...',
          noResults: 'No results found',
          saveSuccess: 'Successfully saved',
          updateSuccess: 'Successfully updated',
          deleteSuccess: 'Successfully deleted'
        },
        validation: {
          required: 'This field is required',
          invalidEmail: 'Invalid email format',
          minLength: 'Must be at least {{count}} characters',
          maxLength: 'Must be at most {{count}} characters',
          passwordMatch: 'Passwords must match',
          number: 'Must be a number',
          positiveNumber: 'Must be a positive number'
        },
        views: {
          view: "View:",
          cards: "Cards",
          table: "Table",
          cardView: "Card view",
          tableView: "Table view"
        },
        invalidDate: "Invalid Date"
      }
    }
  },
  cs: {
    translation: {
      dashboard: {
        title: 'Dashboard',
        myProjects: 'Moje projekty',
        allProjects: 'Všechny projekty',
        newProject: 'Nový projekt',
        searchProjects: 'Hledat projekty...',
        noProjects: 'Nebyly nalezeny žádné projekty',
        showFilters: 'Zobrazit filtry',
        hideFilters: 'Skrýt filtry',
        filterByStatus: 'Filtrovat podle stavu',
        showingProjects: 'Zobrazeno {{filtered}} z {{total}} projektů',
        projectDetails: 'Detaily projektu',
        errors: {
          fetchFailed: 'Nepodařilo se načíst projekty',
          techniciansFetchFailed: 'Nepodařilo se načíst techniky',
          saveFailed: 'Nepodařilo se uložit projekt',
          deleteFailed: 'Nepodařilo se smazat projekt'
        }
      },
      
      workLogs: {
        title: 'Pracovní záznamy',
        logHours: 'Zaznamenat pracovní hodiny',
        confirmDelete: 'Opravdu chcete smazat tento pracovní záznam?',
        deleteFailed: 'Nepodařilo se smazat pracovní záznam',
        saveFailed: 'Nepodařilo se uložit pracovní záznam',
        noProject: 'Není vybrán žádný projekt',
        newWorkLog: 'Nový pracovní záznam',
        editWorkLog: 'Upravit pracovní záznam',
        date: 'Datum',
        hours: 'Hodiny',
        description: 'Popis',
        saving: 'Ukládání...',
        errors: {
          noProject: 'Není vybrán žádný projekt',
          saveWorkLog: 'Nepodařilo se uložit pracovní záznam',
          deleteWorkLog: 'Nepodařilo se smazat pracovní záznam',
          requiredFields: 'Datum a hodiny jsou povinné'
        }
      },
      
      files: {
        filesAndPhotos: 'Soubory a fotografie',
        noFilesForTag: 'Žádné soubory pro vybranou kategorii',
        noFiles: 'Zatím nejsou nahrány žádné soubory',
        noCategories: 'Nejsou nakonfigurovány žádné kategorie souborů',
        title: 'Nahrát soubory',
        category: 'Kategorie souboru',
        selectCategory: 'Vyberte kategorii',
        uploadFiles: 'Nahrát soubory',
        preview: 'Náhled souboru',
        addMore: 'Přidat další',
        takePhoto: 'Vyfotit',
        supportedFormats: 'Obrázky, PDF, dokumenty Office do 10MB',
        description: 'Popis',
        descriptionPlaceholder: 'Přidejte popis pro soubory...',
        uploading: 'Nahrávání...',
        uploadButton: 'Nahrát {{count}} soubor',
        uploadButton_plural: 'Nahrát {{count}} souborů',
        deleteFile: 'Opravdu chcete smazat tento soubor?',
        downloadAll: 'Stáhnout vše',
        download: 'Stáhnout soubor',
        file: 'Soubor',
        textFile: 'Textový soubor',
        unknown: 'Neznámý',
        cannotPreview: 'Tento typ souboru nelze přímo zobrazit. Stáhněte si soubor pro zobrazení.',
        errors: {
          noFiles: 'Vyberte prosím alespoň jeden soubor',
          invalidType: 'Tento typ souboru není podporován',
          maxSize: 'Soubor je příliš velký. Maximální velikost je 10MB',
          uploadFailed: 'Nepodařilo se nahrát soubory',
          deleteFailed: 'Nepodařilo se smazat soubor',
          noResponse: 'Žádná odpověď ze serveru',
          invalidFormat: 'Neplatný formát dat souboru'
        },
        selectFiles: 'Vyberte soubory',
        dropFiles: 'Přetáhněte soubory sem',
        clickToUpload: 'Klikněte pro nahrání souborů',
        orDragDrop: 'nebo přetáhněte',
        supportedFormats: 'Obrázky, PDF, dokumenty Office do 10MB',
        selectedFiles: 'Vybrané soubory',
        
      },header: {
        title: 'Systém správy projektů',
        profile: {
          language: 'Jazyk',
          changePassword: 'Změnit heslo',
          addToHomeScreen: 'Přidat na plochu',
          logout: 'Odhlásit se'
        },
        customers: 'Zákazníci',
        warehouse: {
          title: 'Sklad',
          items: 'Skladové položky',
          materialReceipt: 'Příjem materiálu',
          materialDelivery: 'Výdej materiálu'
        },
        setup: {
          title: 'Nastavení',
          companySetup: 'Nastavení společnosti',
          userManagement: 'Správa uživatelů',
          warehouseManagement: 'Správa skladu'
        },
        languages: {
          en: 'Angličtina',
          cs: 'Čeština'
        }
      },

      auth: {
        login: 'Přihlásit se',
        logout: 'Odhlásit se',
        username: 'Uživatelské jméno',
        password: 'Heslo',
        loginTitle: 'Systém správy projektů',
        loginSubtitle: 'Přihlaste se prosím ke svému účtu',
        errors: {
          invalidCredentials: 'Neplatné přihlašovací údaje',
          accountDeactivated: 'Účet je deaktivován'
        },
        passwordChange: {
          title: 'Změna hesla',
          currentPassword: 'Současné heslo',
          newPassword: 'Nové heslo',
          confirmPassword: 'Potvrdit nové heslo',
          meetRequirements: 'Prosím splňte všechny požadavky na heslo',
          mustBeDifferent: 'Nové heslo musí být odlišné od současného hesla',
          success: 'Heslo bylo úspěšně změněno',
          error: 'Změna hesla se nezdařila',
          changing: 'Měním heslo...',
          submit: 'Změnit heslo',
          requirements: {
            title: 'Požadavky na heslo',
            minLength: 'Minimálně 8 znaků',
            hasUppercase: 'Obsahuje alespoň jedno velké písmeno',
            hasLowercase: 'Obsahuje alespoň jedno malé písmeno',
            hasNumber: 'Obsahuje alespoň jednu číslici',
            hasSpecial: 'Obsahuje alespoň jeden speciální znak',
            matches: 'Hesla se shodují'
          }
        }
      },

      userManagement: {
        title: 'Správa uživatelů',
        newUser: 'Nový uživatel',
        editUser: 'Upravit uživatele',
        users: 'Uživatelé',
        search: 'Hledat uživatele...',
        username: 'Uživatelské jméno',
        password: 'Heslo',
        name: 'Jméno',
        email: 'Email',
        role: 'Systémová role',
        companyRole: 'Firemní role',
        status: 'Stav',
        active: 'Aktivní',
        roles: {
          admin: 'Administrátor',
          officeAdmin: 'Kancelářský správce',
          warehouseAdmin: 'Správce skladu',
          technician: 'Technik'
        },
        noUsersFound: 'Žádní uživatelé nenalezeni.',
        usersTab: 'Uživatelé',
        companyRolesTab: 'Firemní role',
        newCompanyRole: 'Nová firemní role',
        editCompanyRole: 'Upravit firemní roli',
        roleName: 'Název role',
        roleDescription: 'Popis',
        usersWithRole: 'Uživatelé',
        noRolesFound: 'Žádné firemní role nenalezeny.',
        selectCompanyRole: 'Vyberte firemní roli',
        enterCompanyRole: 'Zadejte firemní roli',
        companyRoles: {
          electrician: 'Elektrikář',
          plumber: 'Instalatér',
          hvac: 'HVAC Technik'
        },
        errors: {
          loadUsers: 'Nepodařilo se načíst uživatele',
          saveUser: 'Nepodařilo se uložit uživatele',
          deleteUser: 'Nepodařilo se smazat uživatele'
        }
      },warehouse: {
        title: 'Sklad',
        items: {
          title: 'Skladové položky',
          new: 'Nová položka',
          search: 'Hledat položky...',
          noItems: 'Žádné položky nenalezeny'
        },
        materialReceipt: {
          title: 'Příjemky materiálu',
          new: 'Nová příjemka',
          search: 'Hledat příjemky...',
          noReceipts: 'Žádné příjemky nenalezeny'
        },
        materialDelivery: {
          title: 'Výdejky materiálu',
          new: 'Nová výdejka',
          search: 'Hledat výdejky...',
          noDeliveries: 'Žádné výdejky nenalezeny'
        }
      },

      customers: {
        title: 'Zákazníci',
        new: 'Nový zákazník',
        search: 'Hledat zákazníky...',
        noCustomers: 'Žádní zákazníci nenalezeni',
        type: {
          company: 'Firma',
          personal: 'Osoba'
        },
        form: {
          companyName: 'Název firmy',
          taxId: 'DIČ',
          contactPerson: 'Kontaktní osoba',
          fullName: 'Celé jméno',
          email: 'Email',
          phone: 'Telefon',
          address: {
            title: 'Adresa',
            street: 'Ulice',
            city: 'Město',
            postalCode: 'PSČ',
            country: 'Země'
          }
        }
      },
      
      photos: {
        title: 'Nahrát fotky',
        photoType: 'Typ fotek',
        tags: {
          all: 'Vše',
          inspection: 'Prohlídka',
          implementation: 'Realizace'
        },
        uploadPhotos: 'Fotky',
        preview: 'Náhled fotky',
        addMore: 'Přidat další',
        takePhoto: 'Vyfotit',
        uploadFiles: 'Nahrát soubory',
        supportedFormats: 'PNG, JPG, GIF do 10MB',
        description: 'Popis (platí pro všechny fotky)',
        uploading: 'Nahrávání...',
        uploadButton: 'Nahrát {{count}} fotku',
        uploadButton_plural: 'Nahrát {{count}} fotek',
        errors: {
          invalidType: 'Prosím vyberte pouze obrázkové soubory',
          noPhotos: 'Prosím vyberte alespoň jednu fotku',
          uploadFailed: 'Chyba při nahrávání fotek',
          downloadFailed: 'Nepodařilo se stáhnout fotografie'
        },
        defaultAlt: 'Fotografie projektu',
        deletePhoto: 'Smazat fotografii',
        noPhotosForTag: 'Žádné fotografie pro vybranou kategorii',
        downloadAll: 'Stáhnout vše'
      },
      projects: {
        unnamed: 'Nepojmenovaný projekt',
        startDate: 'Datum zahájení',
        status: 'Stav',
        statusUnknown: 'Neznámý',
        description: 'Popis',
        assignedTechnicians: 'Přiřazení technici',
        customerSignature: 'Podpis zákazníka',
        signatureFor: 'Podpis pro {{name}}',
        addSignature: 'Přidat podpis',
        updateSignature: 'Aktualizovat podpis',
        signatureImage: 'Podpis zákazníka',
        signedOn: 'Podepsáno dne {{date}}',
        noSignature: 'Bez podpisu',
        
        // New project modal translations for Czech
        new: 'Nový projekt',
        edit: 'Upravit projekt',
        name: 'Název projektu',
        contractNumber: 'Číslo smlouvy',
        location: 'Místo realizace',
        deleteProject: 'Smazat projekt',
        selectTechnicians: 'Vyberte techniky...',
        searchTechnicians: 'Hledat techniky...',
        noTechniciansFound: 'Žádní technici nenalezeni',
        techniciansSelected: '{{count}} technik vybrán',
        techniciansSelected_plural: '{{count}} technici vybráni',
        
        // Validation messages
        validation: {
          contractNumberRequired: 'Číslo smlouvy je povinné',
          nameRequired: 'Název projektu je povinný',
          locationRequired: 'Místo realizace je povinné',
          startDateRequired: 'Datum zahájení je povinné'
        },
        
        errors: {
          loadProjects: 'Nepodařilo se načíst projekty',
          loadTechnicians: 'Nepodařilo se načíst techniky',
          saveProject: 'Nepodařilo se uložit projekt',
          deleteProject: 'Nepodařilo se smazat projekt'
        }
      },

      // Company Settings Translations
      companySetup: {
        title: 'Nastavení společnosti',
        description: 'Spravujte základní informace o vaší společnosti, měny a kategorie souborů', // ✅ NOVÝ
        saved: 'Nastavení společnosti úspěšně uloženo!',
        
        sections: {
          companyInfo: 'Informace o společnosti',
          basicInfo: 'Základní informace', // ✅ NOVÝ
          currencySettings: 'Nastavení měny',
          fileCategories: 'Kategorie souborů'
        },
        
        fields: {
          name: 'Název společnosti',
          companyName: 'Název společnosti', // ✅ NOVÝ (alias)
          taxId: 'DIČ',
          address: 'Adresa',
          city: 'Město',
          postalCode: 'PSČ',
          country: 'Země',
          phone: 'Telefon',
          email: 'Email'
        },
        
        buttons: {
          save: 'Uložit nastavení'
        },
        
        messages: {
          saved: 'Nastavení společnosti úspěšně uloženo!' // ✅ NOVÝ
        },
        
        validation: {
          requiredFields: 'Prosím vyplňte všechna povinná pole: {{fields}}',
          defaultCurrency: 'Výchozí měna musí být povolena'
        },
        
        // Zachovat existující překlady...
        companyInfo: 'Informace o společnosti',
        companyName: 'Název společnosti',
        address: 'Adresa',
        city: 'Město',
        postalCode: 'PSČ',
        country: 'Země',
        phone: 'Telefon',
        email: 'Email',
        currencySettings: 'Nastavení měny',
        defaultCurrency: 'Výchozí měna',
        enabledCurrencies: 'Dostupné měny',
        fileTags: 'Kategorie souborů',
        fileTagsTitle: 'Kategorie souborů',
        addTag: 'Přidat kategorii',
        tagCode: 'Kód kategorie',
        englishLabel: 'Anglický název',
        czechLabel: 'Český název',
        missingAllTag: 'Upozornění: Kategorie "Vše" chybí. Tato kategorie je nezbytná pro správnou funkci systému.',
        cannotDelete: 'Nelze smazat systémovou kategorii',
        note: 'Poznámka: Kategorie "Vše" nemůže být smazána, protože je vyžadována systémem. Ostatní kategorie mohou být deaktivovány nebo smazány podle potřeby.',
        saveSettings: 'Uložit nastavení',
        settingsSaved: 'Nastavení společnosti úspěšně uloženo!',
        
        errors: {
          loadSettings: 'Nepodařilo se načíst nastavení společnosti',
          saveSettings: 'Nepodařilo se uložit nastavení společnosti',
          missingFields: 'Vyplňte prosím všechna povinná pole'
        }
      },
      // File Tags Management Translations
      fileTags: {
        title: 'Kategorie souborů',
        addTag: 'Přidat kategorii',
        tagCode: 'Kód kategorie',
        englishLabel: 'Anglický název',
        czechLabel: 'Český název',
        cannotDelete: 'Nelze smazat systémovou kategorii',
        missingAllTag: 'Upozornění: Kategorie "Vše" chybí. Tato kategorie je nezbytná pro správnou funkci systému.',
        note: 'Poznámka: Kategorie "Vše" nemůže být smazána, protože je vyžadována systémem. Ostatní kategorie mohou být deaktivovány nebo smazány podle potřeby.'
      },

      // Currency Management Translations
      currency: {
        defaultCurrency: 'Výchozí měna',
        availableCurrencies: 'Dostupné měny',
        searchCurrencies: 'Hledat měny...'
      },

      common: {
        developerLogo: 'Logo vývojáře',
        customerLogo: 'Logo zákazníka',
        showMore: 'Zobrazit více',
        showLess: 'Zobrazit méně',
        actions: {
          save: 'Uložit',
          cancel: 'Zrušit',
          edit: 'Upravit',
          delete: 'Smazat',
          create: 'Vytvořit',
          update: 'Aktualizovat',
          search: 'Hledat',
          close: 'Zavřít',
          confirm: 'Potvrdit',
          title: 'Akce'
        },
        status: {
          active: 'Aktivní',
          inactive: 'Neaktivní',
          pending: 'Čeká',
          completed: 'Dokončeno',
          onHold: 'Pozastaveno',
          cancelled: 'Zrušeno'
        },
        messages: {
          confirmDelete: 'Opravdu chcete toto smazat?',
          loading: 'Načítání...',
          noResults: 'Nebyly nalezeny žádné výsledky',
          saveSuccess: 'Úspěšně uloženo',
          updateSuccess: 'Úspěšně aktualizováno',
          deleteSuccess: 'Úspěšně smazáno'
        },
        validation: {
          required: 'Toto pole je povinné',
          invalidEmail: 'Neplatný formát emailu',
          minLength: 'Musí mít alespoň {{count}} znaků',
          maxLength: 'Může mít nejvýše {{count}} znaků',
          passwordMatch: 'Hesla se musí shodovat',
          number: 'Musí být číslo',
          positiveNumber: 'Musí být kladné číslo'
        },
        views: {
          view: "Zobrazení:",
          cards: "Karty",
          table: "Tabulka",
          cardView: "Zobrazení karet",
          tableView: "Zobrazení tabulky"
        },
        invalidDate: "Neplatné datum"
      }
    }
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'cs'],
    
    interpolation: {
      escapeValue: false
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;